<script setup lang="ts">
import { computed, defineProps, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { PropType } from "@vue/runtime-core";
import { AddonsItemStruct } from "@/struct/AddonsStruct";
import { formatAmountInput } from "@/utils/format";
import "swiper/css";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";

const props = defineProps({
  option: {
    type: Object as PropType<AddonsItemStruct | null>,
    default: null,
  },
  isAddition: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const getUnit = computed(() => {
  switch (props.option?.unitData?.id) {
    case "sms":
      return t("common.units.sms");
    case "gb":
      return t("common.units.gb");
    case "min":
      return t("common.units.min");
    default:
      return "";
  }
});

const fullness = computed(() => {
  if (!props.option?.unitData) return;

  return Math.floor(
    (props.option.unitData.value / props.option.unitData.maximum) * 100
  );
});
</script>

<template>
  <div class="addons-main-item">
    <div v-if="!isAddition && option" class="addons-main-item__wrapper">
      <div class="addons-main-item__title h4">
        {{ option.title }}
      </div>
      <div v-if="option.unitData" class="addons-main-item__unit">
        <div class="addons-main-item__unit">
          <div
            :style="`width: ${fullness}%`"
            class="addons-main-item__unit-progress"
          ></div>
          <div class="addons-main-item__unit-wrapper">
            <div class="addons-main-item__unit-value h3 color-white">
              <span>{{ Math.round(option.unitData.value) }} {{ getUnit }}</span>
            </div>
            <div
              class="addons-main-item__unit-maximum caption-small normal color-text-tertiary"
            >
              <span
                >{{ t("common.labels.of") }}
                {{ Math.round(option.unitData.maximum) }}</span
              >
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="option.servicesList && option.servicesList.length"
        class="addons-main-item__services"
      >
        <BaseIcon
          v-for="(img, index) in option.servicesList"
          :key="index"
          :name="img"
          folder="service"
          is-original-color
        />
      </div>

      <div class="addons-main-item__description caption-small">
        <span v-if="option.amount"
          >{{ formatAmountInput(option.amount) }}₽</span
        >
        <span v-else>{{ t("common.labels.free") }}</span>
        <span v-if="option.date">
          ,<br />
          {{ t("common.labels.activeUntil") }} {{ option.date }}
        </span>
      </div>
    </div>
    <div v-else class="addons-main-item__wrapper --addition">
      <div class="addons-main-item__addition">
        <BaseFigureForIcon
          icon="plus"
          color="secondary"
          size="small"
          is-button
        />
        <div class="addons-main-item__addition-text button-medium">
          <span>{{ t("addons.item.addOption") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/style/main";

.addons-main-item {
  background-color: var(--color-bg-primary);
  height: 180px;
  border-radius: 18px;
  cursor: pointer;

  @media (max-width: 768px) {
    background-color: var(--color-white);
    height: auto;
  }

  &__wrapper {
    padding: 16px;
    display: grid;
    grid-template-rows: 36px 1fr 30px;
    gap: 12px;
    height: 100%;

    &.--addition {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  }

  &__addition {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;

    &-text {
      width: 76px;
      text-align: center;
    }

    @media (max-width: 768px) {
      flex-direction: row-reverse;

      &-text {
        width: auto;
      }
    }
  }

  &__services {
    margin-top: auto;
    display: flex;
    gap: 4px;
    --icon-size: 28px;
  }

  &__unit {
    width: 100%;
    margin-top: auto;
    border-radius: 16px;
    background: var(--color-bg-secondary);
    position: relative;
    overflow: hidden;

    &-progress {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      background: var(--color-tertiary);
    }

    &-wrapper {
      z-index: 1;
      position: relative;
      padding: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}
</style>
